import {
  createNamespaceFn,
  hasOwn,
  isFunction,
  isPlainObject,
  wphkDesignName,
} from '../shared';
import type { SafeParseReturnType, ZodType } from 'zod';
import {
  createApp,
  isVue3,
  appContext as Vue2,
  type ComponentPublicInstance,
} from 'virtual-vue';

export interface MountInstance {
  instance: ComponentPublicInstance
  unmount: () => void
}

/**
 * 匹配 Vue 3 风格的事件监听器属性名
 * 例如: onClick, onUpdate:visible, onMouseEnter
 */
const EVENT_LISTENER_RE = /^on[A-Z]/;

/**
 * 将 Vue 3 风格的事件名转换为 Vue 2 风格
 * onClick -> click
 * onUpdate:visible -> update:visible
 * onMouseEnter -> mouse-enter (kebab-case)
 */
function toVue2EventName(onEventName: string): string {
  // 移除 'on' 前缀
  const eventName = onEventName.slice(2);
  // 首字母小写
  const firstChar = eventName.charAt(0).toLowerCase();
  const rest = eventName.slice(1);
  // 将 camelCase 转换为 kebab-case (如 MouseEnter -> mouse-enter)
  const kebabCase = (firstChar + rest).replace(/([A-Z])/g, '-$1').toLowerCase();
  return kebabCase;
}

/**
 * 从 props 中分离出事件监听器和普通属性
 * 统一使用 Vue 3 的传入写法 (onXxx)，兼容 Vue 2/2.7
 * 参考: https://v3-migration.vuejs.org/zh/breaking-changes/listeners-removed.html
 */
function separatePropsAndListeners(props: Record<string, any>): {
  propsData: Record<string, any>;
  listeners: Record<string, Function>;
} {
  const propsData: Record<string, any> = {};
  const listeners: Record<string, Function> = {};

  for (const [key, value] of Object.entries(props)) {
    // 检查是否为事件监听器 (onXxx 格式且值为函数)
    if (EVENT_LISTENER_RE.test(key) && typeof value === 'function') {
      const eventName = toVue2EventName(key);
      listeners[eventName] = value;
    } else {
      propsData[key] = value;
    }
  }

  return { propsData, listeners };
}

export function mount(component: any, props: Record<string, any> = {}): MountInstance {
  if (isVue3) {
    // Vue 3: 直接传递 props，事件监听器会自动处理
    const app = createApp(component, props);
    const host = document.createElement('div');
    document.body.appendChild(host);

    return {
      instance: (app as any).mount(host),
      unmount() {
        app.unmount();
        if (host.parentNode) {
          document.body.removeChild(host);
        }
      },
    };
  }

  // Vue 2/2.7: 需要分离 props 和事件监听器
  const { propsData, listeners } = separatePropsAndListeners(props);

  const Constructor = (Vue2 as any)().extend(component);  // 仅传递组件选项
  const instance = new Constructor({
    propsData,  // 通过 propsData 传递 props
  }).$mount();

  // 在 Vue 2 中，通过 $on 方法添加事件监听器
  for (const [eventName, handler] of Object.entries(listeners)) {
    instance.$on(eventName, handler);
  }

  document.body.appendChild(instance.$el);
  return {
    instance,
    unmount() {
      // 移除所有事件监听器
      for (const [eventName, handler] of Object.entries(listeners)) {
        instance.$off(eventName, handler);
      }
      instance.$destroy();
      if (instance.$el.parentNode) {
        document.body.removeChild(instance.$el);
      }
    },
  };
}

export function isZodRule(rule: unknown): rule is ZodType {
  return isPlainObject(rule) && isFunction(rule.safeParseAsync);
}

export function isZodResult(result: unknown): result is SafeParseReturnType<any, any> {
  return isPlainObject(result) && hasOwn(result, 'success');
}

export const createNamespace = createNamespaceFn(wphkDesignName);
